import {
  durationString,
  Shift,
  timeStringFrom24HourTimeString,
} from '@nextforce/api'
import { makeStyles, Text } from '@rneui/themed'
import { font } from 'app/theme'
import { times } from 'lodash'
import React from 'react'
import { View } from 'react-native'

interface Props {
  shift: Shift
}

const MAX_HOURS = 8
const SEGMENTS_PER_HOUR = 4

export function ShiftTimeRangeDurationView({ shift }: Props) {
  const styles = useStyles()

  const actualDuration = shift.isPastShift
    ? shift.actualHours ?? 0
    : shift.duration

  const segmentCount = MAX_HOURS * SEGMENTS_PER_HOUR
  const segmentOnCount = Math.floor(
    Math.min(actualDuration, MAX_HOURS) * SEGMENTS_PER_HOUR
  )

  return (
    <View style={styles.container}>
      {!shift.isPastShift && (
        <View style={styles.times}>
          <Text style={styles.startTime}>
            {timeStringFrom24HourTimeString(shift.startTime)}
          </Text>
          <Text style={styles.endTime}>
            {timeStringFrom24HourTimeString(shift.endTime)}
          </Text>
        </View>
      )}
      <View style={styles.bar}>
        {times(segmentCount, index => {
          if (actualDuration > MAX_HOURS) {
            const ellipsisIndex2 = (MAX_HOURS / 2) * SEGMENTS_PER_HOUR
            const ellipsisIndex1 = ellipsisIndex2 - SEGMENTS_PER_HOUR

            if (index === ellipsisIndex1 || index === ellipsisIndex2) {
              return (
                <Text key={String(index)} style={styles.ellipsis}>
                  ᠁
                </Text>
              )
            }

            if (
              index > ellipsisIndex1 &&
              index < ellipsisIndex2 + SEGMENTS_PER_HOUR
            ) {
              return null
            }
          }

          return (
            <Segment
              key={index}
              on={index < segmentOnCount}
              endHour={
                index < segmentCount - 1 &&
                (index + 1) % SEGMENTS_PER_HOUR === 0
              }
            />
          )
        })}
        <Text style={styles.duration}>{`(${
          actualDuration === 0 ? '0 Hrs' : durationString(actualDuration)
        })`}</Text>
      </View>
    </View>
  )
}

function Segment(params: { on: boolean; endHour: boolean }) {
  const styles = useSegmentStyles(params)
  return <View style={styles.segment} />
}

const WIDTH = 300
const SEGMENT_SPACE = 5
const DURATION_WIDTH = 75
const DURATION_MARGIN = 15

const useStyles = makeStyles(theme => ({
  container: {
    width: WIDTH,
  },
  times: {
    marginRight: DURATION_WIDTH + DURATION_MARGIN,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  startTime: {
    fontSize: 15,
    fontFamily: font.bold,
    color: theme.colors?.black,
  },
  endTime: {
    fontSize: 15,
    color: theme.colors?.black,
    textAlign: 'right',
  },
  duration: {
    width: DURATION_WIDTH,
    fontSize: 15,
    color: theme.colors?.black,
    marginLeft: DURATION_MARGIN,
  },
  bar: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  ellipsis: {
    width: SEGMENT_WIDTH * SEGMENTS_PER_HOUR,
    color: theme.colors?.primary,
    fontSize: 20,
    marginRight: SEGMENT_SPACE,
  },
}))

const SEGMENT_WIDTH =
  (WIDTH - DURATION_WIDTH - DURATION_MARGIN - (MAX_HOURS - 1) * SEGMENT_SPACE) /
  (SEGMENTS_PER_HOUR * MAX_HOURS)

const useSegmentStyles = makeStyles(
  (theme, params: { on: boolean; endHour: boolean }) => ({
    segment: {
      width: SEGMENT_WIDTH,
      height: 5,
      backgroundColor: params.on ? theme.colors?.primary : theme.colors?.grey5,
      marginRight: params.endHour ? SEGMENT_SPACE : 0,
    },
  })
)
